<template>
<div class="wrapper">
  <div :class="['address-container', {'up-top': isEnabled && keyword}]">
    <div class="address-container__form">
      <one-line-form :submit="updateAddress" :submittable="isValid" :saving="isSaving ? address : ''">
        <input-field name="onelineaddress"
                      id="onelineaddress"
                      type="text"
                      :title="isEnabled ? '' : i18n.t('postalCode:address')"
                      :placeholder="i18n.t('postalCode:address_enter')"
                      :autocapitalize="true"
                      :autocorrect="false"
                      :value="isEnabled ? keyword : address"
                      ref="input"
                      @keyDown="keydown"
                      @input="getPredictions" />
        <transition name="fade">
          <div class="slot-holder" v-show="isEnabled ? !keyword : true">
            <slot></slot>
          </div>
        </transition>
      </one-line-form>
    </div>
    <transition name="fade">
      <div class="address-container__predictions-container" @touchstart="hideKeyboard" v-if="isEnabled && predictions.length > 0">
        <div class="address-container__predictions-container__list">
          <ul>
            <li v-for="(prediction, index) in predictions" :class="{selected: selectedAnswerIndex === index}" :key="'prediction-'+index" @click="syncAddress(index)">
              <span>{{prediction.structured_formatting.main_text}}</span>
              <small>{{prediction.structured_formatting.secondary_text}}</small>
            </li>
          </ul>
          <img v-show="keyword && predictions.length > 0" src="~static/powered_by_google.png" id="credit">
        </div>
      </div>
    </transition>
    <div v-if="isEnabled && keyword && predictions.length <= 0" class="address-container__predictions-container__warning">
      <helper :error="true">{{ i18n.t('postalCode:incorrect_address') }}</helper>
    </div>
  </div>
</div>
</template>

<script>
  import Vue from 'vue'
  import { mapState } from 'vuex'

  export default Vue.component('one-line-address-screen', {
    props: ['screenParams'],
    data () {
      const address = this.$store.state.user.address
      const isEnabled = this.$store.state.partner.googleAutocompleteKey
      return {
        selectedAnswerIndex: -1,
        address: address || '',
        keyword: '',
        isSaving: false,
        autocomplete: isEnabled && new window.google.maps.places.AutocompleteService(),
        service: isEnabled && new window.google.maps.places.PlacesService(document.createElement('input')),
        predictions: [],
        warning: false,
        typingTimeout: null,
        submittable: (!isEnabled && true) || false,
        isEnabled: isEnabled && true
      }
    },
    computed: {
      ...mapState(['app', 'partner']),
      isValid () {
        return this.isEnabled ? this.submittable : typeof this.address === 'string' && this.address.length > 4
      }
    },
    methods: {
      keydown (e) {
        if (e.keyCode === 40) {
          e.preventDefault()
          if (this.selectedAnswerIndex === this.predictions.length - 1) return
          this.selectedAnswerIndex++
        } else if (e.keyCode === 38) {
          e.preventDefault()
          if (this.selectedAnswerIndex === -1) {
            this.selectedAnswerIndex = 0
          } else if (this.selectedAnswerIndex === 0) return
          this.selectedAnswerIndex--
        } else if (e.keyCode === 13 && this.selectedAnswerIndex !== -1) {
          this.syncAddress()
        }
      },
      syncAddress (index) {
        this.selectedAnswerIndex = index !== undefined ? index : this.selectedAnswerIndex
        this.address = this.predictions[this.selectedAnswerIndex].description
        this.updateAddress()
      },
      async updateAddress () {
        if (!this.isEnabled) return this.setManualAddress()
        const addressData = await this.getAddressDetails()
        this.isSaving = true
        this.submittable = true
        this.$store.dispatch('updateUser', addressData)
        setTimeout(() => {
          this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        }, (400))
      },
      getPredictions (value) {
        if (!this.isEnabled) {
          this.address = value
          return
        }
        clearTimeout(this.typingTimeout)
        this.typingTimeout = setTimeout(() => {
          this.keyword = value
          if (!value) {
            this.predictions = []
            return
          }
          this.autocomplete.getPlacePredictions({
            input: value,
            componentRestrictions: { country: this.app.country.code },
            types: ['address']
          }, (predictions, status) => {
            this.selectedAnswerIndex = -1
            this.predictions = predictions || []
          })
        }, 200)
      },
      hideKeyboard () {
        document.activeElement.blur()
      },
      getAddressDetails () {
        let promise = new Promise((resolve, reject) => {
          this.service.getDetails({
            placeId: this.predictions[this.selectedAnswerIndex].place_id
          }, (place, status) => {
            if (status === 'OK') {
              resolve({
                address: place.formatted_address,
                houseNumber: this.findProperty(place.address_components, ['street_number']),
                streetName: this.findProperty(place.address_components, ['street_address', 'route']),
                postalCode: this.findProperty(place.address_components, ['postal_code']) || (this.app.country.postalCodePattern && '0'.repeat(this.app.country.postalCodePattern.length)) || this.app.unknownPostcode,
                city: this.findProperty(place.address_components, ['locality', 'administrative_area_level_1', 'administrative_area_level_2', 'sublocality', 'administrative_area_level_3', 'administrative_area_level_4', 'administrative_area_level_5']),
                country: this.app.country.code,
                province: this.getProvinceCode(place),
                provinceCode: this.getProvinceCode(place) ? 'CA-' + this.getProvinceCode(place) : '',
                provinceName: this.findProperty(place.address_components, ['administrative_area_level_1', 'administrative_area_level_2', 'sublocality', 'administrative_area_level_3', 'administrative_area_level_4', 'administrative_area_level_5']),
              })
            }
          })
        })

        return promise
      },
      findProperty (target, keys) {
        let property
        keys.some(key => {
          property = target.find(element => element.types.includes(key))
          if (property) return true
        })
        return property ? property.long_name : ''
      },
      setManualAddress () {
        this.isSaving = true
        let answerData = {}
        answerData['address'] = this.address
        this.$store.dispatch('updateUser', answerData)
        this.$store.dispatch('next', this.screenParams.nextScreenRoute)
      },
      findProvince (provinceName) {
        if (provinceName && this.app.country.provinces) {
          const provinceMatch = this.app.country.provinces.find((province) => {
            return province.name === provinceName
          })
          return provinceMatch.ISOCode ? provinceMatch.ISOCode.slice(-2) : ''
        } else {
          return provinceName || ''
        }
      },
      getProvinceCode (place) {
        return this.findProvince(this.findProperty(place.address_components, ['administrative_area_level_1', 'administrative_area_level_2', 'sublocality', 'administrative_area_level_3', 'administrative_area_level_4', 'administrative_area_level_5']))
      },
    }
  })
</script>

<style lang="scss" scoped>
@import '../assets/mixins';
@import '../assets/settings';
@import '../assets/animations';

.wrapper {
  position: absolute;
  top: -30px;
  bottom: 0;
  left: -30px;
  right: -30px;
  margin-top: -30%;
  display: flex;
  overflow: hidden;
  flex-direction: column;

  @include desktop {
    top: -63px;
    height: 500px;
  }

  @include desktop-big {
    top: -76px;
    height: 600px;
    width: 610px;
    left: -115px;
  }
}

.address-container {
  padding: 30px 0;
  margin-top: 25%;
  position: relative;
  z-index: 20;
  transition: all .4s ease-out;

  @include desktop{
    margin-top: $section-desktop-margin-top;
    position: static;
  }

  @include desktop-big{
    margin-top: 160px;
  }

  &__form {
    transition: all .4s ease-out;
    z-index: 21;
    position: relative;
    padding: 0 30px;

    @include desktop {
      position: static;
    }

    @include desktop-big {
      padding: 0 110px;
    }
  }


  &__predictions-container {
    z-index: 20;
    position: relative;
    width: 100%;
    box-sizing: border-box;
    padding-top: 20px;
    overflow-y: auto;
    @include scrollbar;

    &__list {
      min-height: 290px;
      position: relative;
      padding-bottom: 40px;

      img#credit {
        width: 150px;
        object-fit: contain;
        position: absolute;
        left: 0;
        right: 0;
        margin: auto;
        bottom: 0px;
      }
    }

    &__warning {
      padding: 0 30px;
      margin-top: 20px;
      animation: fade-in .4s 1s backwards;
    }

    ul {
      transition: all .4s;
      li {
        cursor: pointer;
        height: 58px;
        width: 100%;
        padding: 10px 0;
        list-style: none;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: center;
        position: relative;
        border-bottom: 1px #F1F1F1 solid;

        &:last-child {
          border-color: transparent;
        }

        &.selected {
          background: $inputSaveColor;
          border-color: $inputSaveColor;
          span,
          small {
            color: #FFFFFF;
          }
        }
        span,
        small {
          padding: 0 30px;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;

          @include desktop-big {
            padding: 0 110px;
          }
        }
        span {
          color: #3D3D3D;
          font-size: 16px;
          font-weight: bold;
          line-height: 20px;
        }
        small {
          color: #5B687A;
          font-size: 14px;
          line-height: 17px;
        }
      }
    }
  }

  &.up-top {
    margin-top: 0;
    padding-top: 0;

    .address-container__form {
      padding: 10px 30px;
      box-shadow:  0px 2px 20px 0px rgba(0, 0, 0, 0.09);
      background: #FFFFFF;

      @include desktop-big {
        padding: 10px 110px;
      }

      /deep/ .input-wrapper {
        border-color: transparent;
      }
      /deep/ .underlay {
        .input.saving {
            overflow: hidden !important;
            text-overflow: ellipsis;
          }
      }

      form {
        margin: 0;
      }
    }
  }


  /deep/ .button-wrapper {
    right: 0;
    top: 0;
    bottom: 0;
    overflow: hidden;
    margin: auto;

    @include desktop {
      top: auto;
    }
    
    @include desktop-big {
      bottom: 30px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/OneLineAddress.vue